import React, { useState } from "react";
import {
    BellOff,
    Mail,
    Smartphone,
    Monitor,
    ShoppingCart,
    FileText,
    BookOpen,
    AlertTriangle,
    Clock,
    Globe,
    Heart,
    Truck,
    BarChart2,
    TrendingDown,
    TrendingUp,
    Users,
    Wrench,
    CalendarCheck,
    Zap,
    LayoutGrid,
} from "lucide-react";
import { Panel } from "../../stock/StockUI";
import { SaveAction } from "./SettingsUI";
import {tabClass, tabIconClass} from '../Common/TabBar';

/* ─── Toggle — flat pill ─────────────────────────────────── */
const Toggle = ({ checked, onChange }) => (
    <button
        onClick={onChange}
        type="button"
        aria-pressed={checked}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            checked ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
        }`}
    >
        <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-150 ${
                checked ? "translate-x-5" : "translate-x-0.5"
            }`}
        />
    </button>
);

/* ─── Row ─────────────────────────────────────────────────── */
const NotificationRow = ({ icon: Icon, title, description, checked, onChange }) => (
    <div className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
        <div className="flex min-w-0 items-start gap-3 sm:items-center">
            <Icon size={16} className="text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</p>
                {description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
                )}
            </div>
        </div>
        <Toggle checked={checked} onChange={onChange} />
    </div>
);

/* ─── Section block ───────────────────────────────────────── */
const Section = ({ title, children }) => <Panel title={title}>{children}</Panel>;

/* ─── Channel picker ──────────────────────────────────────── */
const ChannelPicker = ({ settings, toggle }) => (
    <Section title="Canal de notification">
        <div className="grid grid-cols-1 gap-2 px-4 sm:px-6 py-3 sm:flex sm:flex-wrap">
            {[
                { key: "emailEnabled", label: "Email",  icon: Mail },
                { key: "pushEnabled",  label: "Push",   icon: Smartphone },
                { key: "inAppEnabled", label: "In-app", icon: LayoutGrid },
            ].map(({ key, label, icon: Icon }) => (
                <button
                    key={key}
                    type="button"
                    onClick={() => toggle(key)}
                    aria-pressed={settings[key]}
                    className={`inline-flex w-full items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors sm:w-auto ${
                        settings[key]
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-gray-400"
                    }`}
                >
                    <Icon size={13} />
                    {label}
                </button>
            ))}
        </div>
    </Section>
);

/* ─── Frequency picker ────────────────────────────────────── */
const FrequencyPicker = ({ settings, setSettings }) => (
    <Section title="Fréquence">
        <div className="grid grid-cols-1 gap-2 px-4 sm:px-6 py-3 sm:flex sm:flex-wrap">
            {[
                { value: "realtime", label: "Temps réel" },
                { value: "hourly",   label: "Toutes les heures" },
                { value: "daily",    label: "Quotidien" },
            ].map(({ value, label }) => (
                <button
                    key={value}
                    type="button"
                    onClick={() => setSettings((p) => ({ ...p, frequency: value }))}
                    aria-pressed={settings.frequency === value}
                    className={`w-full px-3 py-2 rounded-lg border text-xs font-medium transition-colors sm:w-auto ${
                        settings.frequency === value
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-gray-400"
                    }`}
                >
                    {label}
                </button>
            ))}
        </div>
    </Section>
);

/* ─── History ─────────────────────────────────────────────── */
const NotifHistory = ({ entries }) => (
    <Section title="Historique — 7 derniers jours">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {entries.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 px-4 sm:px-6 py-3">
                    {item.unread && (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    )}
                    {!item.unread && <span className="w-1.5 h-1.5 flex-shrink-0" />}
                    <item.icon size={14} className={item.color} />
                    <div className="flex-1 min-w-0">
                        <p className={`text-xs break-words sm:truncate ${item.unread ? "font-semibold text-gray-800 dark:text-gray-100" : "text-gray-600 dark:text-gray-400"}`}>
                            {item.title}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 sm:hidden">
                            <span className="text-xs text-gray-400">{item.time}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-md px-1.5 py-0.5">
                                {item.tag}
                            </span>
                        </div>
                    </div>
                    <span className="hidden sm:inline text-xs text-gray-400 flex-shrink-0 ml-2">{item.time}</span>
                    <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-md px-1.5 py-0.5 flex-shrink-0">
                        {item.tag}
                    </span>
                </div>
            ))}
        </div>
    </Section>
);

/* ─── Module header bar ───────────────────────────────────── */
const ModuleBar = ({ icon: Icon, title, badge }) => (
    <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 min-w-0">
            <Icon size={18} className="text-primary flex-shrink-0" />
            <h3 className="text-base font-semibold text-gray-800 dark:text-white truncate">{title}</h3>
        </div>
        <span className="text-xs font-semibold px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
            {badge}
        </span>
    </div>
);

const ModuleSelectorButton = ({ module, isActive, onSelect }) => {
    const { label, icon: Icon, active } = module;

    return (
        <button
            type="button"
            onClick={() => active && onSelect(module.key)}
            disabled={!active}
            aria-pressed={isActive}
            className={`h-16 ${
                active
                    ? tabClass(isActive)
                    : `${tabClass(false)} !cursor-not-allowed !text-gray-400 dark:!text-gray-500 !border-transparent`
            }`}
        >
            <Icon className={tabIconClass} />
            {label}
            {!active && (
                <span className="text-[10px] text-gray-400 border border-gray-200 dark:border-gray-600 rounded-md px-1.5 py-0.5">
                    Soon
                </span>
            )}
        </button>
    );
};

/* ═══════════════════════════════════════════════════════════
   CAISSE PANEL
═══════════════════════════════════════════════════════════ */
const CaissePanel = ({ settings, toggle, setSettings }) => (
    <div className="space-y-4">
        <ModuleBar icon={Monitor} title="Caisse enregistreuse" badge="Actif" />

        <ChannelPicker settings={settings} toggle={toggle} />

        <Section title="Alertes de stock">
            <NotificationRow
                icon={AlertTriangle}
                title="Stock faible"
                description="Alerte quand le stock passe sous le seuil minimum"
                checked={settings.stockLow}
                onChange={() => toggle("stockLow")}
            />
            <NotificationRow
                icon={BellOff}
                title="Rupture de stock"
                description="Alerte en cas de stock à zéro"
                checked={settings.stockOut}
                onChange={() => toggle("stockOut")}
            />
        </Section>

        <Section title="Ventes & Tickets">
            <NotificationRow
                icon={ShoppingCart}
                title="Nouvelles ventes"
                description="Notification à chaque vente enregistrée"
                checked={settings.sales}
                onChange={() => toggle("sales")}
            />
            <NotificationRow
                icon={FileText}
                title="Annulation de ticket"
                description="Alerte lors de l'annulation d'un ticket"
                checked={settings.ticketCancellation}
                onChange={() => toggle("ticketCancellation")}
            />
        </Section>

        <Section title="Journal de caisse">
            <NotificationRow
                icon={BookOpen}
                title="Ouverture de journal"
                description="Notification lors de l'ouverture de la caisse"
                checked={settings.journalOpen}
                onChange={() => toggle("journalOpen")}
            />
            <NotificationRow
                icon={BookOpen}
                title="Fermeture de journal"
                description="Notification lors de la clôture de la caisse"
                checked={settings.journalClose}
                onChange={() => toggle("journalClose")}
            />
        </Section>

        <FrequencyPicker settings={settings} setSettings={setSettings} />

        <NotifHistory entries={[
            { icon: AlertTriangle, color: "text-amber-500", title: "Stock faible — Café arabica",  time: "Il y a 2h",      tag: "Stock",   unread: true  },
            { icon: FileText,      color: "text-red-500",   title: "Ticket #1042 annulé",           time: "Il y a 5h",      tag: "Ticket",  unread: true  },
            { icon: BookOpen,      color: "text-blue-400",  title: "Ouverture journal — Caisse 1",  time: "Hier 08:30",     tag: "Journal", unread: false },
            { icon: BellOff,       color: "text-red-400",   title: "Rupture de stock — Sucre",      time: "Il y a 2 jours", tag: "Stock",   unread: false },
        ]} />

        <SaveAction onClick={() => {}} />
    </div>
);

/* ═══════════════════════════════════════════════════════════
   QUICKCOM 360 PANEL
═══════════════════════════════════════════════════════════ */
const Q360Panel = ({ settings, toggle, setSettings }) => (
    <div className="space-y-4">
        <ModuleBar icon={Globe} title="QuickCom 360" badge="Actif" />

        <ChannelPicker settings={settings} toggle={toggle} />

        <Section title="Rapports & Analytics">
            <NotificationRow
                icon={BarChart2}
                title="Rapport journalier"
                description="Résumé quotidien de vos indicateurs clés"
                checked={settings.dailyReport}
                onChange={() => toggle("dailyReport")}
            />
            <NotificationRow
                icon={CalendarCheck}
                title="Rapport hebdomadaire"
                description="Synthèse de la semaine chaque lundi matin"
                checked={settings.weeklyReport}
                onChange={() => toggle("weeklyReport")}
            />
            <NotificationRow
                icon={Zap}
                title="Résumé mensuel"
                description="Performance globale et comparaison mois précédent"
                checked={settings.monthlyReport}
                onChange={() => toggle("monthlyReport")}
            />
        </Section>

        <Section title="Alertes de performance">
            <NotificationRow
                icon={TrendingUp}
                title="Objectif de vente atteint"
                description="Notification quand l'objectif journalier est dépassé"
                checked={settings.targetReached}
                onChange={() => toggle("targetReached")}
            />
            <NotificationRow
                icon={TrendingDown}
                title="Baisse de chiffre d'affaires"
                description="Alerte si le CA chute de plus de 20 % vs la veille"
                checked={settings.revenueDropAlert}
                onChange={() => toggle("revenueDropAlert")}
            />
            <NotificationRow
                icon={Users}
                title="Nouveaux clients"
                description="Notification lors de l'enregistrement d'un nouveau client"
                checked={settings.newCustomer}
                onChange={() => toggle("newCustomer")}
            />
        </Section>

        <Section title="Système">
            <NotificationRow
                icon={Wrench}
                title="Mises à jour disponibles"
                description="Alertes lors de nouvelles versions de l'application"
                checked={settings.systemUpdates}
                onChange={() => toggle("systemUpdates")}
            />
            <NotificationRow
                icon={Clock}
                title="Maintenance programmée"
                description="Préavis avant toute interruption de service"
                checked={settings.maintenance}
                onChange={() => toggle("maintenance")}
            />
        </Section>

        <FrequencyPicker settings={settings} setSettings={setSettings} />

        <NotifHistory entries={[
            { icon: BarChart2,  color: "text-violet-500", title: "Rapport journalier généré",           time: "Auj. 08:00",    tag: "Rapport", unread: true  },
            { icon: TrendingUp, color: "text-green-500",  title: "Objectif journalier atteint — 112 %", time: "Il y a 3h",     tag: "Perf",    unread: true  },
            { icon: Users,      color: "text-cyan-500",   title: "3 nouveaux clients enregistrés",      time: "Hier",          tag: "Clients", unread: false },
            { icon: Wrench,     color: "text-gray-400",   title: "Mise à jour v2.4.1 disponible",       time: "Il y a 2 jours",tag: "Système", unread: false },
        ]} />

        <SaveAction onClick={() => {}} />
    </div>
);

/* ─── Coming soon ─────────────────────────────────────────── */
const ComingSoonPanel = ({ label }) => (
    <div className="flex flex-col items-center text-center py-16">
        <Clock size={48} className="text-gray-300 dark:text-gray-600 mb-4" />
        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">En cours de développement</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
            Les notifications <strong className="text-gray-700 dark:text-gray-300">{label}</strong> seront disponibles prochainement.
        </p>
        <span className="mt-4 text-xs border border-gray-200 dark:border-gray-600 rounded-full text-gray-500 dark:text-gray-400 px-3 py-1">
            Bientôt disponible
        </span>
    </div>
);

/* ─── Modules config ──────────────────────────────────────── */
const MODULES = [
    { key: "caisse",    label: "Caisse enregistreuse", icon: Monitor,      active: true  },
    { key: "360",       label: "QuickCom 360",          icon: Globe,        active: true  },
    { key: "ecommerce", label: "E-commerce",            icon: ShoppingCart, active: false },
    { key: "track",     label: "QuickCom Track",        icon: Truck,        active: false },
    { key: "fidelite",  label: "QuickCom Fidélité",     icon: Heart,        active: false },
];

const INITIAL_CAISSE = {
    emailEnabled: true, pushEnabled: false, inAppEnabled: true,
    stockLow: true, stockOut: true, sales: false,
    ticketCancellation: true, journalOpen: false, journalClose: false,
    frequency: "realtime",
};

const INITIAL_360 = {
    emailEnabled: true, pushEnabled: true, inAppEnabled: true,
    dailyReport: true, weeklyReport: true, monthlyReport: false,
    targetReached: true, revenueDropAlert: true, newCustomer: false,
    systemUpdates: true, maintenance: true,
    frequency: "daily",
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const NotificationsSettings = () => {
    const [activeModule, setActiveModule] = useState("caisse");
    const [caisseSettings, setCaisseSettings] = useState(INITIAL_CAISSE);
    const [q360Settings,   setQ360Settings]   = useState(INITIAL_360);

    const toggleCaisse = (key) => setCaisseSettings((p) => ({ ...p, [key]: !p[key] }));
    const toggle360    = (key) => setQ360Settings((p)   => ({ ...p, [key]: !p[key] }));

    return (
        <div>
            {/* Platforms as underlined tabs, like the catalogue tabs */}
            <div className="flex overflow-x-auto no-scrollbar sm:px-2 border-b border-gray-200 dark:border-gray-700">
                {MODULES.map((module) => (
                    <ModuleSelectorButton
                        key={module.key}
                        module={module}
                        isActive={activeModule === module.key}
                        onSelect={setActiveModule}
                    />
                ))}
            </div>

            <div className="max-w-4xl p-4 sm:p-6">
                {activeModule === "caisse" && (
                    <CaissePanel
                        settings={caisseSettings}
                        toggle={toggleCaisse}
                        setSettings={setCaisseSettings}
                    />
                )}
                {activeModule === "360" && (
                    <Q360Panel
                        settings={q360Settings}
                        toggle={toggle360}
                        setSettings={setQ360Settings}
                    />
                )}
                {!["caisse", "360"].includes(activeModule) && (
                    <ComingSoonPanel
                        label={MODULES.find((m) => m.key === activeModule)?.label}
                    />
                )}
            </div>
        </div>
    );
};

export default NotificationsSettings;
